"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";

export function PropertySkeletonList() {
  return (
    <div className="space-y-8">
      {/* URL Input Skeleton with integrated credits */}
      <Card className="bg-white overflow-hidden border shadow-sm">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <Skeleton className="h-7 w-44" />
            <Skeleton className="h-7 w-36 rounded-full bg-gray-100 border border-gray-200" />
          </div>
          <Skeleton className="h-4 w-full max-w-md mb-6" />
          <Skeleton className="h-10 w-full max-w-3xl" />
        </div>
      </Card>

      {/* Title Skeleton */}
      <Skeleton className="h-7 w-48 mb-4" />

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <motion.div
            key={`skeleton-grid-item-${i}-${Date.now()}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: Math.min(i * 0.05, 0.5) }}
          >
            <PropertySkeleton />
          </motion.div>
        ))}
      </div>

      {/* Loading More Skeleton */}
      <div className="col-span-full flex justify-center py-8">
        <Skeleton className="h-8 w-8 rounded-full" />
      </div>
    </div>
  );
}

function PropertySkeleton() {
  return (
    <Card className="overflow-hidden border border-gray-200 relative h-full">
      {/* Image skeleton with 16:9 aspect ratio */}
      <div className="relative">
        <Skeleton className="aspect-[16/9] w-full rounded-none" />

        {/* Price skeleton */}
        <div className="absolute bottom-3 left-3">
          <Skeleton className="h-6 w-24 rounded-sm" />
        </div>

        {/* Property features skeleton */}
        <div className="absolute bottom-3 right-3 flex items-center gap-3">
          <Skeleton className="h-5 w-10 rounded-sm" />
          <Skeleton className="h-5 w-10 rounded-sm" />
          <Skeleton className="h-5 w-10 rounded-sm" />
        </div>

        {/* Location badge skeleton */}
        <div className="absolute left-3 top-3">
          <Skeleton className="h-6 w-28 rounded-full" />
        </div>

        {/* AI badge skeleton */}
        <div className="absolute right-3 top-3">
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
      </div>

      <CardContent className="p-3 pb-4">
        {/* Address skeleton */}
        <Skeleton className="h-5 w-3/4" />

        {/* Analysis preview skeleton */}
        <div className="flex gap-2 mt-2 mb-1">
          <Skeleton className="h-3.5 w-3.5 mt-0.5 rounded-full flex-shrink-0" />
          <Skeleton className="h-3.5 w-full" />
        </div>

        {/* Footer skeleton */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <div className="flex gap-1.5">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-3.5 w-3.5 rounded-full" />
        </div>
      </CardContent>
    </Card>
  );
}
